import './meta-fatigue-insights.scss';
import cn from 'classnames';
import { ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import {
  MdArrowBack,
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
  MdNotificationsActive,
  MdOutlineVideoLibrary,
  MdPhoto,
  MdSlideshow
} from 'react-icons/md';
import { abbreviateNumber, Formatter } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { CPM_FORMATTER, CPX_FORMATTER } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { DatePicker } from '@Components/DatePicker';
import { Dropdown } from '@Components/Dropdown';
import {
  FetchFilterBar,
  hasFilter,
  useFilterPaneState
} from '@Components/FetchFilterBar/FetchFilterBar';
import { IconButton } from '@Components/IconButton';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv, InteractiveSpan } from '@Components/InteractiveEl';
import { KpiPicker, useKpiPickerData } from '@Components/KpiPicker';
import { buildCrossChannelKpiMap } from '@Components/KpiPicker/kpi-picker-utils';
import { MetricsTable } from '@Components/MetricsTable/MetricsTable';
import { CellData } from '@Components/MetricsTable/metrics-table-utils';
import { Spinner } from '@Components/Spinner/Spinner';
import { Page } from '@Components/Page';
import { TableEmptyState } from '@Components/TableEmptyState';
import { Appearance, Design, Size } from '@Components/designConstants';
import { FilterPreset } from '@blisspointmedia/bpm-types/dist/pgTables/FilterPreset';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { useSetError } from '../../redux/modals';
import { GetS3SignedUrl2, MetaLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { Danger500, Neutral500, Success600 } from '../../utils/colors';
import { buildAdSetsFromLiveData } from './meta-fatigue-data';
import {
  Alert,
  buildFilterCategories,
  DEFAULT_DATE_RANGE,
  DEMO_COMPANY,
  DEMO_KPIS,
  DEMO_THUMBNAIL_KEY,
  DIVERSITY_PNG_URL,
  META_FATIGUE_HELP_URL,
  FILTER_PLATFORM,
  FilterSource,
  FilterSourceType,
  DiversityAdSet,
  FatigueAd,
  FatigueAdSet,
  FOCUS_SERIES,
  Ad,
  AdSet,
  Tab,
  TAB_NAVS,
  TabType
} from './meta-fatigue-insights-constants';
import {
  buildAlerts,
  computeHeatDomains,
  DiversityLevelType,
  emptyStateMessage,
  formatCtr,
  formatKpiRevenue,
  formatKpiVolume,
  formatAdSetStatus,
  formatPlatformDetail,
  formatRoas,
  formatSpend,
  filterAdSets,
  getDiversityAdSets,
  getDiversityLevel,
  getExposure,
  getFatigueAdSets,
  getLowDiversitySets,
  getPlatformRank,
  getHeatColor,
  getWeightedCpx,
  HeatDomain,
  HeatMetricType,
  latestFatigueInWindow,
  PLATFORM_TIER_LABEL,
  FATIGUE_SORT_OPTIONS,
  FatigueSortKey,
  FatigueSortKeyType,
  sortFatigueAdSets,
  statusSortValueFor
} from './meta-fatigue-insights-utils';
import { FatigueTimeChart } from './FatigueTimeChart';
import { FocusChart } from './FocusChart';
import { QuadrantChart } from './QuadrantChart';
import {
  useAdKpiVolumeRevenue,
  useAdPerformanceTotals,
  useAdSetDailyPerformance,
  useAdSetPerformanceTotals,
  useAdsetPlacementBreakdown,
  useAdsetStatus,
  useCreativeFatigueSummary,
  useCreativeFatiguedAds,
  useDiversityLabelScore
} from './use-meta-fatigue-live-data';

type HeatDomains = Record<HeatMetricType, HeatDomain>;

const sumSpend = (adSets: AdSet[]): number => adSets.reduce((sum, s) => sum + s.spend, 0);

// Shared stable empty array — both MetricsTable panels below pass this instead of a fresh
// `[]` literal each render, so it's not a new reference on every parent re-render.
const EMPTY_AGGREGATE: CellData[] = [];

const CREATIVE_BUCKET_NAME = 'bpm-cdn';

// ── Table height cap ─────────────────────────────────────────────────────────
const TABLE_MAX_HEIGHT_FLOOR = 360;
const TABLE_MAX_HEIGHT_VIEWPORT_OFFSET = 420;

const computeTableMaxHeight = (): number =>
  Math.max(TABLE_MAX_HEIGHT_FLOOR, window.innerHeight - TABLE_MAX_HEIGHT_VIEWPORT_OFFSET);

const useTableMaxHeight = (): number => {
  const [maxHeight, setMaxHeight] = useState(computeTableMaxHeight);

  useEffect(() => {
    const handleResize = () => setMaxHeight(computeTableMaxHeight());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return maxHeight;
};

// ── Metric card ────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
  tooltip?: ReactNode; // definition shown on hover, for a metric whose meaning isn't self-evident
  onClick?: () => void; // when set, the card becomes a clickable shortcut to a tab
}

const MetricCard = ({ label, value, sub, warn = false, tooltip, onClick }: MetricCardProps) => {
  const body = (
    <>
      <div className='bp-metric__label'>
        {label}
        {tooltip && (
          <InfoTooltip size={Size.SM} inline>
            {tooltip}
          </InfoTooltip>
        )}
      </div>
      <div className={cn('bp-metric__value', { 'bp-metric__value--warn': warn })}>{value}</div>
      {sub && <div className='bp-metric__sub'>{sub}</div>}
    </>
  );

  if (!onClick) return <div className='bp-metric'>{body}</div>;

  return (
    <InteractiveDiv className='bp-metric bp-metric--clickable' onClick={onClick}>
      {body}
      <span className='bp-metric__cue'>View details →</span>
    </InteractiveDiv>
  );
};

// ── Modal shell ──────────────────────────────────────────────────────────────
// All three modals share this header. Extracted so the close button can't style-drift
// between them and so it has an accessible name in one place (it is icon-only).

interface ModalHeaderProps {
  title: ReactNode;
  subtitles?: ReactNode[];
}

const BpModalHeader = ({ title, subtitles = [] }: ModalHeaderProps) => (
  <Modal.Header className='bp-modal__header' closeButton>
    <div>
      <Modal.Title className='bp-modal__title'>{title}</Modal.Title>
      {subtitles.map((subtitle, i) => (
        <div key={i} className='bp-modal__subtitle'>
          {subtitle}
        </div>
      ))}
    </div>
  </Modal.Header>
);

// ── Alert banner ───────────────────────────────────────────────────────────

interface AlertBannerProps {
  alert: Alert;
  onOpen: () => void;
  onDismiss: () => void;
}

const AlertBanner = ({ alert, onOpen, onDismiss }: AlertBannerProps) => (
  <div className='bp-alert'>
    <InteractiveDiv className='bp-alert__main' onClick={onOpen}>
      <span className='bp-alert__icon'>
        <MdNotificationsActive />
      </span>
      <p className='bp-alert__text'>{alert.summary}</p>
      <span className='bp-alert__cue'>View affected ad sets →</span>
    </InteractiveDiv>
    <Button
      appearance={Appearance.EMPTY}
      design={Design.NEUTRAL}
      size={Size.SM}
      onClick={onDismiss}
    >
      <MdClose />
    </Button>
  </div>
);

// ── Quadrant metrics guide ─────────────────────────────────────────────────

// Each item is a ReactNode rather than a plain string, because two of them carry links.
// The `id` is the list key: a ReactNode cannot key itself the way the old strings did.
interface GuideItem {
  id: string;
  content: ReactNode;
}

const buildQuadrantGuideItems = (onOpenDiversityPdf: () => void): GuideItem[] => [
  { id: 'scale', content: 'Diversity and fatigue metrics are measured on a scale of 0 to 1.' },
  {
    id: 'diversity-range',
    content:
      'For diversity, 0 is minimum diversity and 1 is maximum. Meta recommends targeting a diversity score between 0.4 and 0.7 for each ad set. Below 0.4 and above 0.7 are not recommended.'
  },
  {
    id: 'diversity-use',
    content:
      "We recommend using the diversity metrics to determine whether each ad set's existing creative mix is differentiated and checking the impact of each new creative launch on diversity. Creative diversity is core to Meta's algorithm and low diversity ad sets risk lower auction priority leading to faster fatigue and poor performance."
  },
  {
    id: 'fatigue-range',
    content:
      'For Fatigue, 0 is minimum fatigue and 1 is maximum fatigue. Generally, lower fatigue numbers are better.'
  },
  {
    id: 'fatigue-use',
    content:
      "When an ad set has a high fatigue score, it generally means that new creative is needed, not that high fatigue creatives need to be paused. Sometimes a high-performing creative may be more efficient than other creatives while still experiencing high fatigue. Always review a creative's performance holistically and avoid pausing creatives simply due to a high fatigue score."
  },
  {
    id: 'diversity-no-history',
    content:
      'Diversity scores do not currently have historical values. Filtering by date will update delivery, performance and fatigue metrics but not diversity metrics.'
  },
  {
    id: 'funnel',
    content:
      'Per Meta, fatigue metrics are not applicable to upper funnel campaigns. We recommend using fatigue scores primarily for lower funnel campaigns and directionally for mid funnel campaigns.'
  },
  {
    id: 'ad-types',
    content:
      "Diversity and fatigue scores don't apply to certain ad types, including catalog ads and DCO/ACO ads. In addition, fatigue scores only apply to single image/video ads and not carousel or collection ads. Ads must be older than 1 day and have more than 1,000 impressions to qualify for a fatigue score. Any significant edits to a campaign will reset the fatigue scores of ads within that campaign."
  },
  {
    id: 'scope',
    content:
      'An ad set needs at least one of the two scores to appear on the quadrant chart. If it has only one, the missing metric is plotted as 0 and the bubble is drawn hollow, so a missing reading never reads as a real 0 — diversity scores of 0 are real values. Ad sets with neither score are left out entirely.'
  },
  {
    id: 'rubric',
    content: (
      <>
        Meta calculates fatigue using the following rubric: &quot;When we believe that your audience
        has seen the same ad too many times, your fatigue score will increase. We consider all
        recent exposures of the ad&apos;s image or video, including those from other campaigns from
        your Page. We also consider your ad&apos;s cost per result.&quot; For more info, see
        Meta&apos;s help documentation{' '}
        {/* A real anchor, not an onClick: it is an external destination, so this keeps
            middle-click, cmd-click and "copy link address" working, and needs no JS. */}
        <a
          className='bp-metrics-guide__link'
          href={META_FATIGUE_HELP_URL}
          target='_blank'
          rel='noreferrer'
        >
          here
        </a>
        .
      </>
    )
  },
  {
    id: 'diversity-pdf',
    content: (
      <>
        For detailed information on how Meta calculates diversity scores,{' '}
        <InteractiveSpan className='bp-metrics-guide__link' onClick={onOpenDiversityPdf}>
          see here
        </InteractiveSpan>
        .
      </>
    )
  }
];

// The overview renders as a plain image scaled to fit the modal, so the whole export is
// legible the moment it opens. The "open in a new tab" link stays as the escape hatch: it
// serves the file at full resolution for anyone who needs to read the fine print or zoom.
interface DiversityOverviewModalProps {
  show: boolean;
  onHide: () => void;
  container: RefObject<HTMLDivElement | null>;
}

const DiversityOverviewModal = ({ show, onHide, container }: DiversityOverviewModalProps) => (
  <Modal
    show={show}
    onHide={onHide}
    container={container}
    dialogClassName='bp-modal bp-modal--doc'
    size='xl'
    centered
  >
    <BpModalHeader
      title='How Meta calculates diversity scores'
      subtitles={[
        <a href={DIVERSITY_PNG_URL} target='_blank' rel='noreferrer'>
          Open in a new tab
        </a>
      ]}
    />
    <Modal.Body className='bp-modal__body'>
      {/* An <img>, not an <iframe>. A browser renders a standalone image inside a frame
          at its natural pixel size — Chrome's image viewer even opens it zoomed — so a
          large export overflowed and appeared cropped. An img scales to its box. */}
      <img
        className='bp-doc-image'
        src={DIVERSITY_PNG_URL}
        alt="Meta's explanation of how creative diversity scores are calculated"
      />
    </Modal.Body>
  </Modal>
);

interface QuadrantMetricsGuideProps {
  container: RefObject<HTMLDivElement | null>;
}

const QuadrantMetricsGuide = ({ container }: QuadrantMetricsGuideProps) => {
  const [showPdf, setShowPdf] = useState(false);
  const items = useMemo(() => buildQuadrantGuideItems(() => setShowPdf(true)), []);

  return (
    <div className='bp-metrics-guide'>
      <div className='bp-metrics-guide__title'>How to interpret these metrics</div>
      <ul className='bp-metrics-guide__list'>
        {items.map(item => (
          <li key={item.id}>{item.content}</li>
        ))}
      </ul>
      <DiversityOverviewModal
        show={showPdf}
        onHide={() => setShowPdf(false)}
        container={container}
      />
    </div>
  );
};

// ── Combined panel ─────────────────────────────────────────────────────────

interface CombinedPanelProps {
  adSets: AdSet[];
  campaigns: string[];
  isolatedAdSetId: string | null;
  onIsolate: (adSetId: string) => void;
  onResetIsolate: () => void;
  onNavigate: (tab: TabType) => void;
  dateRange: DateRange;
  filtersActive: boolean;
  isLoading: boolean;
  // Forwarded to the metrics guide's PDF modal, so it portals into the page-scoped
  // container like every other modal here and picks up .meta-fatigue-insights styles.
  container: RefObject<HTMLDivElement | null>;
}

const CombinedPanel = ({
  adSets,
  campaigns,
  isolatedAdSetId,
  onIsolate,
  onResetIsolate,
  onNavigate,
  dateRange,
  filtersActive,
  isLoading,
  container
}: CombinedPanelProps) => {
  const fatigueAdSets = getFatigueAdSets(adSets);
  const lowDiversitySets = getLowDiversitySets(adSets);
  // adSets includes fatigue-only (NA-diversity) ad sets now — this card's copy says "with
  // a diversity label", so it has to count the diversity-scoped subset, not the full list.
  const diversityAdSets = getDiversityAdSets(adSets);
  const isolatedSet =
    isolatedAdSetId === null
      ? null
      : (fatigueAdSets.find(s => s.adSetId === isolatedAdSetId) ?? null);
  const weightedCpx = getWeightedCpx(adSets);

  return (
    <div className='bp-panel'>
      <div className='bp-metrics-grid bp-metrics-grid--4'>
        <MetricCard
          label='Ad sets tracked'
          value={String(diversityAdSets.length)}
          sub='with a diversity label'
        />
        <MetricCard
          label='With fatigue history'
          value={String(fatigueAdSets.length)}
          sub={`${formatSpend(sumSpend(fatigueAdSets))} spend`}
          onClick={() => onNavigate(Tab.FATIGUE)}
        />
        <MetricCard
          label='Low-diversity ad sets'
          value={String(lowDiversitySets.length)}
          sub={`${formatSpend(sumSpend(lowDiversitySets))} spend`}
          warn
          onClick={() => onNavigate(Tab.DIVERSITY)}
        />
        <MetricCard
          label='CPX · covered sets'
          value={weightedCpx === null ? '—' : CPX_FORMATTER(weightedCpx)}
          sub='blended avg'
        />
      </div>

      <ChartContainer
        title={
          isolatedSet ? `Fatigue over time · ${isolatedSet.name}` : 'Fatigue over time · by ad set'
        }
        subHeader={
          isolatedSet
            ? 'Fatigue & performance for this set'
            : 'Auto-scaled · click a legend entry to focus'
        }
        minHeight={isolatedSet ? 184 : 320}
        rightActions={
          isolatedSet && (
            <Button
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              onClick={onResetIsolate}
            >
              <MdArrowBack /> Back to all sets
            </Button>
          )
        }
        viewRightActions={Boolean(isolatedSet)}
        isLoading={isLoading}
      >
        <div className='bp-chart-layout'>
          <div className='bp-chart-main'>
            {isolatedSet ? (
              <>
                <div className='bp-focus-legend'>
                  {FOCUS_SERIES.map(series => (
                    <span key={series.key}>
                      <span className='legend-swatch' style={{ background: series.color }} />
                      {series.label}
                    </span>
                  ))}
                </div>
                <div className='bp-focus-chart'>
                  <FocusChart adSet={isolatedSet} dateRange={dateRange} />
                </div>
              </>
            ) : (
              <FatigueTimeChart
                adSets={fatigueAdSets}
                onSelectLine={onIsolate}
                dateRange={dateRange}
                filtersActive={filtersActive}
              />
            )}
          </div>
        </div>
      </ChartContainer>

      <QuadrantChart
        adSets={adSets}
        campaigns={campaigns}
        dateRange={dateRange}
        filtersActive={filtersActive}
        isLoading={isLoading}
      />
      <QuadrantMetricsGuide container={container} />
    </div>
  );
};

// ── Diversity panel ────────────────────────────────────────────────────────

interface DiversityPanelProps {
  adSets: AdSet[];
  onViewCreatives: (adSet: DiversityAdSet) => void;
  filtersActive: boolean;
  isLoading: boolean;
}

// Maps a diversity severity level to the cell / tag styling.
const LEVEL_TAG_CLASS: Record<DiversityLevelType, string> = {
  danger: 'bp-tag--red',
  warn: 'bp-tag--amber',
  ok: 'bp-tag--green'
};

// ── Diversity table columns ──────────────────────────────────────────────────
const DIVERSITY_DIMENSION_COLUMNS: DimensionColumn[] = [
  {
    id: 'name',
    dimensionTypeName: 'Ad set',
    dimensionVarName: 'Ad set',
    label: 'Ad set',
    divider: true
  }
];

const DIVERSITY_DATA_COLUMNS: Column[] = [
  { id: 'status', dataVarName: 'status', label: 'Status' },
  {
    id: 'exposure',
    dataVarName: 'exposure',
    label: 'Spend-weighted diversity',
    heatMapping: { colorScheme: 'sequential', minIsBest: true }
  },
  { id: 'platforms', dataVarName: 'platforms', label: 'Platform diversity' },
  { id: 'activeAds', dataVarName: 'activeAds', label: 'Active ads' },
  {
    id: 'diversity',
    dataVarName: 'diversity',
    label: 'Diversity score',
    heatMapping: { colorScheme: 'sequential' }
  },
  { id: 'spend', dataVarName: 'spend', label: 'Spend' },
  {
    id: 'kpiVolume',
    dataVarName: 'kpiVolume',
    label: 'KPI Volume',
    heatMapping: { colorScheme: 'sequential' }
  },
  {
    id: 'kpiRevenue',
    dataVarName: 'kpiRevenue',
    label: 'KPI Revenue',
    heatMapping: { colorScheme: 'sequential' }
  },
  {
    id: 'cpm',
    dataVarName: 'cpm',
    label: 'CPM',
    heatMapping: { colorScheme: 'sequential', minIsBest: true }
  },
  { id: 'roas', dataVarName: 'roas', label: 'ROAS', heatMapping: { colorScheme: 'sequential' } },
  { id: 'ctr', dataVarName: 'ctr', label: 'CTR', heatMapping: { colorScheme: 'sequential' } }
] satisfies Column[];

const DIVERSITY_COLUMN_META = {
  status: {
    prettyName: 'Status',
    overlayText:
      "Delivery status. Based on the ad set's effective status, which folds in campaign-level pausing. Green = actively delivering, red = paused or not delivering. ⚠️ Source table is incomplete — some ad sets have no status row at all."
  },
  exposure: {
    prettyName: 'Spend-weighted diversity',
    overlayText:
      'Spend weighted by the diversity label (LOW x1, MEDIUM x0.5, HIGH x0.25). A low-diversity set carrying heavy spend ranks highest — where a creative stall would cost the most.'
  },
  platforms: {
    prettyName: 'Platform diversity',
    overlayText:
      'Platform tier — Best (automatic placements, Meta delivers everywhere), Good (2+ publishers across 2+ differentiated surfaces), Low (pinned to one publisher or one surface), — (not reported). Hover a value for the observed publisher/placement breakdown.'
  },
  kpiVolume: {
    prettyName: 'KPI Volume',
    overlayText:
      'Conversions attributed to this set for the KPI selected above. Reads 0 if the selected KPI has no Meta mapping configured for this company.'
  },
  kpiRevenue: {
    prettyName: 'KPI Revenue',
    overlayText:
      'Revenue attributed to this set for the KPI selected above. Reads 0 if the selected KPI has no Meta mapping configured for this company.'
  }
};

// Compact display for the spend-weighted exposure score (e.g. 93500 → "94K").
const formatExposure = (value: number): string => abbreviateNumber(value, 0);

const statusColorFor = (active: boolean | null): string => {
  if (active === null) return Neutral500;
  return active ? Success600 : Danger500;
};

const buildRowFromColumns = <TCtx,>(
  columns: { id: string }[],
  builders: Record<string, (ctx: TCtx) => CellData>,
  ctx: TCtx,
  tableName: string
): CellData[] =>
  columns.map(column => {
    const buildCell = builders[column.id];
    if (!buildCell) {
      throw new Error(`No cell builder registered for ${tableName} column "${column.id}"`);
    }
    return buildCell(ctx);
  });

const DIVERSITY_CELL_BUILDERS: Record<string, (adSet: DiversityAdSet) => CellData> = {
  status: adSet => {
    const active = adSet.status?.active ?? null;
    return {
      label: formatAdSetStatus(adSet),
      value: statusSortValueFor(active),
      textColor: statusColorFor(active)
    };
  },
  exposure: adSet => ({ label: formatExposure(getExposure(adSet)), value: getExposure(adSet) }),
  platforms: adSet => ({
    label: adSet.platforms === null ? '—' : PLATFORM_TIER_LABEL[adSet.platforms.tier],
    value: getPlatformRank(adSet),
    overlayText: formatPlatformDetail(adSet)
  }),
  activeAds: adSet => ({ label: String(adSet.activeAds), value: adSet.activeAds }),
  diversity: adSet => ({ label: adSet.diversityScore.toFixed(2), value: adSet.diversityScore }),
  spend: adSet => ({ label: formatSpend(adSet.spend), value: adSet.spend }),
  cpm: adSet => ({ label: CPM_FORMATTER(adSet.cpm ?? 0), value: adSet.cpm ?? 0 }),
  roas: adSet => ({ label: formatRoas(adSet.roas ?? 0), value: adSet.roas ?? 0 }),
  ctr: adSet => ({ label: formatCtr(adSet.ctr ?? 0), value: adSet.ctr ?? 0 }),
  kpiVolume: adSet => ({
    label: formatKpiVolume(adSet.kpiVolume ?? 0),
    value: adSet.kpiVolume ?? 0
  }),
  kpiRevenue: adSet => ({
    label: formatKpiRevenue(adSet.kpiRevenue ?? 0),
    value: adSet.kpiRevenue ?? 0
  })
};

const buildDiversityTableData = (
  adSets: DiversityAdSet[],
  onViewCreatives: (adSet: DiversityAdSet) => void
): { dimensionData: Record<string, CellData>[]; tableData: CellData[][] } => {
  const dimensionData: Record<string, CellData>[] = [];
  const tableData: CellData[][] = [];

  for (const adSet of adSets) {
    const level = getDiversityLevel(adSet.diversityLabel);

    dimensionData.push({
      'Ad set': {
        label: adSet.name,
        value: adSet.name,
        content: (
          <div className='bp-diversity-name-cell'>
            <InteractiveSpan
              className='bp-table__name bp-table__name--link'
              onClick={() => onViewCreatives(adSet)}
            >
              {adSet.name}
            </InteractiveSpan>
            <span className={cn('bp-tag', LEVEL_TAG_CLASS[level])}>{adSet.diversityLabel}</span>
          </div>
        )
      }
    });

    tableData.push(
      buildRowFromColumns(DIVERSITY_DATA_COLUMNS, DIVERSITY_CELL_BUILDERS, adSet, 'diversity')
    );
  }

  return { dimensionData, tableData };
};

const DiversityPanel = ({
  adSets,
  onViewCreatives,
  filtersActive,
  isLoading
}: DiversityPanelProps) => {
  const tableMaxHeight = useTableMaxHeight();
  // adSets includes fatigue-only (NA-diversity) ad sets now — this whole tab only makes
  // sense for ad sets with a real diversity reading, so scope once, up front.
  const diversityAdSets = getDiversityAdSets(adSets);
  const lowDiversitySets = getLowDiversitySets(adSets);
  const totalSpend = sumSpend(diversityAdSets);
  const avgDiversityScore =
    diversityAdSets.reduce((sum, adSet) => sum + adSet.diversityScore, 0) /
    Math.max(1, diversityAdSets.length);

  const { dimensionData, tableData } = buildDiversityTableData(diversityAdSets, onViewCreatives);

  return (
    <div className='bp-panel'>
      <div className='bp-metrics-grid bp-metrics-grid--3'>
        <MetricCard
          label='Ad sets tracked'
          value={String(diversityAdSets.length)}
          sub={`${formatSpend(totalSpend)} combined`}
        />
        <MetricCard
          label='Low diversity ad sets'
          value={String(lowDiversitySets.length)}
          sub={`${formatSpend(sumSpend(lowDiversitySets))} spend`}
          warn
        />
        <MetricCard
          label='Average diversity score'
          value={avgDiversityScore.toFixed(2)}
          sub='across tracked ad sets'
        />
      </div>

      <ChartContainer
        title='Diversity · ranked by exposure'
        footerContent='Click an ad set name to see its active creatives. Diversity is reported as a single fixed-window snapshot, and spend-based columns are totals for the full export — neither responds to the date picker.'
        minHeight={isLoading ? 450 : undefined}
        isLoading={isLoading}
      >
        <div className='bp-chart-contents'>
          {diversityAdSets.length === 0 ? (
            <TableEmptyState
              message={emptyStateMessage(
                filtersActive,
                'No ad sets have a diversity label for this account.'
              )}
            />
          ) : (
            <div className='bp-metrics-table-viewport'>
              <MetricsTable
                dimensionColumns={DIVERSITY_DIMENSION_COLUMNS}
                dimensionData={dimensionData}
                dataColumns={DIVERSITY_DATA_COLUMNS}
                tableData={tableData}
                columnMetaDataMap={DIVERSITY_COLUMN_META}
                aggregateData={EMPTY_AGGREGATE}
                defaultSorting={[{ id: 'exposure', asc: false }]}
                maxHeight={tableMaxHeight}
                overrideParams={{
                  colWidth: [90, 160, 110, 100, 110, 90, 110, 110, 100, 100, 100],
                  rowHeight: 74
                }}
              />
            </div>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

// ── Fatigue table columns ───────────────────────────────────────────────────
// All performance KPIs are shown as columns (no picker). Each carries its own
// formatting and color-scale, so cells get conditional formatting.
interface MetricBearer {
  roas: number | null;
  cpm: number | null;
  ctr: number | null;
  cpx: number | null;
  cpc: number | null;
  kpiVolume: number | null;
  kpiRevenue: number | null;
}

interface FatigueMetric {
  // The key is the field name AND the column id.
  key: keyof MetricBearer;
  label: string;
  format: Formatter;
  minIsBest?: boolean;
}

const FATIGUE_METRICS: FatigueMetric[] = [
  { key: 'kpiVolume', label: 'KPI Volume', format: formatKpiVolume },
  { key: 'kpiRevenue', label: 'KPI Revenue', format: formatKpiRevenue },
  { key: 'roas', label: 'ROAS', format: formatRoas },
  { key: 'cpx', label: 'CPX', format: CPX_FORMATTER, minIsBest: true },
  { key: 'cpc', label: 'CPC', format: CPX_FORMATTER, minIsBest: true },
  { key: 'cpm', label: 'CPM', format: CPM_FORMATTER, minIsBest: true },
  { key: 'ctr', label: 'CTR', format: formatCtr }
];

const FATIGUE_DIMENSION_COLUMNS: DimensionColumn[] = [
  {
    id: 'fatigueAdSet',
    dimensionTypeName: 'Fatigue Ad Set',
    dimensionVarName: 'Fatigue Ad Set',
    label: 'Ad set',
    divider: true
  },
  {
    id: 'fatigueAdName',
    dimensionTypeName: 'Fatigue Ad Name',
    dimensionVarName: 'Fatigue Ad Name',
    label: 'Ad name'
  }
];

const FATIGUE_DATA_COLUMNS: Column[] = [
  { id: 'status', dataVarName: 'status', label: 'Status' },
  {
    id: 'fatigue',
    dataVarName: 'fatigue',
    label: 'Fatigue',
    heatMapping: { colorScheme: 'sequential', minIsBest: true }
  },
  { id: 'shareOfSpend', dataVarName: 'shareOfSpend', label: 'Share of spend' },
  { id: 'spend', dataVarName: 'spend', label: 'Spend' },
  ...FATIGUE_METRICS.map((metric): Column => ({
    id: metric.key,
    dataVarName: metric.key,
    label: metric.label,
    heatMapping: {
      colorScheme: 'sequential',
      ...(metric.minIsBest ? { minIsBest: true } : {})
    }
  }))
] satisfies Column[];

const FATIGUE_COLUMN_META = {
  fatigue: {
    prettyName: 'Fatigue',
    overlayText:
      'Fatigue score for this ad set as of the selected date range. Darker = healthier (lower fatigue). Reported only at the ad-set level — individual ads don’t carry their own score.'
  },
  status: {
    prettyName: 'Status',
    overlayText:
      "Delivery status. Ad-level status isn't sourced yet — ad rows show a neutral placeholder rather than a real value."
  }
};

// ── Fatigue table row model ─────────────────────────────────────────────────

interface FatigueRowContext {
  kind: 'adSet' | 'ad';
  adSet: FatigueAdSet;
  ad?: FatigueAd;
  fatigueLatest: number | null; // only meaningful when kind === 'adSet'
}

const metricCellBuilder =
  (metric: FatigueMetric) =>
  (ctx: FatigueRowContext): CellData => {
    const bearer: MetricBearer = ctx.kind === 'ad' && ctx.ad ? ctx.ad : ctx.adSet;
    const value = bearer[metric.key];
    return value === null
      ? { label: metric.format(0), value: '--' }
      : { label: metric.format(value), value };
  };

const FATIGUE_CELL_BUILDERS: Record<string, (ctx: FatigueRowContext) => CellData> = {
  status: ctx => {
    // TODO: Per-ad status has no source yet. See the ad-status note in this page's CLAUDE.md.
    if (ctx.kind === 'ad') {
      return { label: '', value: statusSortValueFor(null) };
    }
    const active = ctx.adSet.status?.active ?? null;
    return {
      label: formatAdSetStatus(ctx.adSet),
      value: statusSortValueFor(active),
      textColor: statusColorFor(active)
    };
  },
  // Fatigue is stored per ad set, not per ad — no per-ad score exists.
  fatigue: ctx => {
    if (ctx.kind === 'ad' || ctx.fatigueLatest === null) {
      return { label: '—', value: '--' };
    }
    return { label: ctx.fatigueLatest.toFixed(2), value: ctx.fatigueLatest };
  },
  shareOfSpend: ctx => {
    if (ctx.kind !== 'ad' || !ctx.ad) return { label: '—', value: '--' };
    return { label: `${Math.round(ctx.ad.spendShare * 100)}%`, value: ctx.ad.spendShare };
  },
  spend: ctx => {
    if (ctx.kind === 'ad' && ctx.ad) {
      return { label: formatSpend(ctx.ad.spend), value: ctx.ad.spend };
    }
    return { label: formatSpend(ctx.adSet.spend), value: ctx.adSet.spend, bold: true };
  },
  ...Object.fromEntries(FATIGUE_METRICS.map(metric => [metric.key, metricCellBuilder(metric)]))
};

const buildFatigueRow = (ctx: FatigueRowContext): CellData[] =>
  buildRowFromColumns(FATIGUE_DATA_COLUMNS, FATIGUE_CELL_BUILDERS, ctx, 'fatigue');

const FATIGUE_ADSET_ROW_HEIGHT = 78;
const FATIGUE_AD_ROW_HEIGHT = 54;
const FATIGUE_DIMENSION_COL_WIDTH = [180, 180];
const FATIGUE_DATA_COL_WIDTH = [85, 85, 100, 90, 110, 110, 100, 100, 100, 100, 100];

const buildFatigueTableData = (
  adSets: FatigueAdSet[],
  expanded: Set<string>,
  dateRange: DateRange,
  onToggleAdSet: (id: string) => void,
  onViewAd: (ad: FatigueAd, adSet: FatigueAdSet) => void
): { dimensionData: Record<string, CellData>[]; tableData: CellData[][]; rowHeights: number[] } => {
  const dimensionData: Record<string, CellData>[] = [];
  const tableData: CellData[][] = [];
  const rowHeights: number[] = [];
  const BLANK: CellData = { label: '', value: '' };

  for (const adSet of adSets) {
    const isExpanded = expanded.has(adSet.adSetId);
    const fatigueLatest = latestFatigueInWindow(adSet, dateRange);

    dimensionData.push({
      'Fatigue Ad Set': {
        label: adSet.name,
        value: adSet.name,
        content: (
          <div className='bp-fatigue-adset-cell'>
            <InteractiveSpan
              className='bp-fatigue-adset-cell__toggle'
              onClick={() => onToggleAdSet(adSet.adSetId)}
            >
              {isExpanded ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}
              <span className='bp-table__name'>{adSet.name}</span>
            </InteractiveSpan>
            {adSet.fatigueFlagged && (
              <span className='bp-tag bp-tag--red'>
                Fatigue-flagged · CPR {CPX_FORMATTER(adSet.costPerResult ?? 0)}
              </span>
            )}
          </div>
        )
      },
      'Fatigue Ad Name': BLANK
    });
    tableData.push(buildFatigueRow({ kind: 'adSet', adSet, fatigueLatest }));
    rowHeights.push(FATIGUE_ADSET_ROW_HEIGHT);

    if (!isExpanded) continue;

    for (const ad of adSet.ads) {
      dimensionData.push({
        'Fatigue Ad Set': BLANK,
        'Fatigue Ad Name': {
          label: ad.name,
          value: ad.name,
          content: (
            <div className='bp-fatigue-adname-cell'>
              <InteractiveSpan
                className='bp-table__name bp-table__name--link'
                onClick={() => onViewAd(ad, adSet)}
              >
                {ad.name}
              </InteractiveSpan>
              {/* commenting this out until we have real ad formats */}
              {/* <span className='bp-ad-meta'>{ad.format}</span> */}
            </div>
          )
        }
      });
      tableData.push(buildFatigueRow({ kind: 'ad', adSet, ad, fatigueLatest: null }));
      rowHeights.push(FATIGUE_AD_ROW_HEIGHT);
    }
  }

  return { dimensionData, tableData, rowHeights };
};

// Sort controls for the fatigue table. These exist because the table's rows are grouped
// (an ad-set row followed by its own ad rows), so MetricsTable's clickable column headers
// are switched off — this is the affordance that replaces them.
interface FatigueSortControlsProps {
  sortKey: FatigueSortKeyType;
  onSortKeyChange: (key: FatigueSortKeyType) => void;
  ascending: boolean;
  onToggleDirection: () => void;
}

const FatigueSortControls = ({
  sortKey,
  onSortKeyChange,
  ascending,
  onToggleDirection
}: FatigueSortControlsProps) => (
  <div className='bp-table-sort'>
    <span className='bp-table-sort__label'>Sort by</span>
    <Dropdown
      size={Size.SM}
      value={sortKey}
      options={FATIGUE_SORT_OPTIONS}
      onChange={onSortKeyChange}
      ariaLabel='Sort the fatigue table by'
    />
    <IconButton
      size={Size.SM}
      appearance={Appearance.OUTLINED}
      design={Design.NEUTRAL}
      onClick={onToggleDirection}
      aria-label={
        ascending
          ? 'Sorted ascending — switch to descending'
          : 'Sorted descending — switch to ascending'
      }
    >
      {ascending ? <MdArrowUpward /> : <MdArrowDownward />}
    </IconButton>
  </div>
);

// ── Fatigue panel ──────────────────────────────────────────────────────────

interface FatiguePanelProps {
  adSets: AdSet[];
  onViewAd: (ad: FatigueAd, adSet: FatigueAdSet) => void;
  dateRange: DateRange;
  filtersActive: boolean;
  isLoading: boolean;
}

const FatiguePanel = ({
  adSets,
  onViewAd,
  dateRange,
  filtersActive,
  isLoading
}: FatiguePanelProps) => {
  const tableMaxHeight = useTableMaxHeight();
  // Everything collapsed on load; the ad-set rows are the overview.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // Sorting is owned here rather than by MetricsTable, whose flat-row sort would separate
  // ad rows from their parent ad set. Defaults to spend descending, the order this table
  // shipped with. See sortFatigueAdSets in meta-fatigue-insights-utils.ts.
  const [sortKey, setSortKey] = useState<FatigueSortKeyType>(FatigueSortKey.SPEND);
  const [sortAscending, setSortAscending] = useState(false);
  const sortedFatigueAdSets = useMemo(
    () => sortFatigueAdSets(getFatigueAdSets(adSets), dateRange, sortKey, sortAscending),
    [adSets, dateRange, sortKey, sortAscending]
  );

  const toggleAdSet = useCallback(
    (id: string) =>
      setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      }),
    []
  );

  // Fatigue is the one metric here with a daily series, so it follows the date picker.
  // The KPI columns are whole-window aggregates and deliberately do not.
  const latestValues = sortedFatigueAdSets
    .map(adSet => latestFatigueInWindow(adSet, dateRange))
    .filter((value): value is number => value !== null);
  const avgFatigue = latestValues.length
    ? latestValues.reduce((sum, value) => sum + value, 0) / latestValues.length
    : null;
  const weightedCpx = getWeightedCpx(sortedFatigueAdSets);

  const { dimensionData, tableData, rowHeights } = useMemo(
    () => buildFatigueTableData(sortedFatigueAdSets, expanded, dateRange, toggleAdSet, onViewAd),
    [sortedFatigueAdSets, expanded, dateRange, toggleAdSet, onViewAd]
  );

  // dimensionColWidth/colWidth are static; only rowHeight varies with the data above.
  const overrideParams = useMemo(
    () => ({
      dimensionColWidth: FATIGUE_DIMENSION_COL_WIDTH,
      colWidth: FATIGUE_DATA_COL_WIDTH,
      rowHeight: rowHeights
    }),
    [rowHeights]
  );

  return (
    <div className='bp-panel'>
      <div className='bp-metrics-grid bp-metrics-grid--3'>
        <MetricCard
          label='Ad sets tracked'
          value={String(sortedFatigueAdSets.length)}
          sub={`${formatSpend(sumSpend(sortedFatigueAdSets))} spend`}
        />
        <MetricCard
          label='Latest fatigue · avg'
          value={avgFatigue === null ? '—' : avgFatigue.toFixed(2)}
          sub='across tracked sets'
        />
        <MetricCard
          label='CPX · these sets'
          value={weightedCpx === null ? '—' : CPX_FORMATTER(weightedCpx)}
          sub='blended avg'
        />
      </div>

      <ChartContainer
        title='Ad set fatigue trends'
        minHeight={isLoading ? 450 : undefined}
        isLoading={isLoading}
        rightActions={
          <FatigueSortControls
            sortKey={sortKey}
            onSortKeyChange={setSortKey}
            ascending={sortAscending}
            onToggleDirection={() => setSortAscending(previous => !previous)}
          />
        }
      >
        <div className='bp-chart-contents'>
          {sortedFatigueAdSets.length === 0 ? (
            <TableEmptyState
              message={emptyStateMessage(
                filtersActive,
                'No ad sets have fatigue history for this account.'
              )}
            />
          ) : (
            <div className='bp-metrics-table-viewport'>
              <MetricsTable
                dimensionColumns={FATIGUE_DIMENSION_COLUMNS}
                dimensionData={dimensionData}
                dataColumns={FATIGUE_DATA_COLUMNS}
                tableData={tableData}
                columnMetaDataMap={FATIGUE_COLUMN_META}
                aggregateData={EMPTY_AGGREGATE}
                // Off because the rows are grouped: an ad-set row is followed by its
                // own ad rows, and MetricsTable sorts a flat index list, which would
                // scatter ads away from their parent. FatigueSortControls above is the
                // replacement affordance; sortFatigueAdSets does the ordering.
                sortable={false}
                maxHeight={tableMaxHeight}
                overrideParams={overrideParams}
              />
            </div>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

// ── Creatives modal ────────────────────────────────────────────────────────

const formatIcon = (format: string) => {
  if (format === 'Video') return <MdOutlineVideoLibrary />;
  if (format === 'Carousel') return <MdSlideshow />;
  return <MdPhoto />;
};

// browser cache so reopening a modal for a creative/ad already seen this session
// shows it instantly instead of re-signing/re-fetching !
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const makeSessionCache = <T,>() => {
  const cache = new Map<string, CacheEntry<T>>();
  return {
    get: (key: string): T | undefined => {
      const entry = cache.get(key);
      if (!entry) return undefined;
      if (Date.now() > entry.expiresAt) {
        cache.delete(key);
        return undefined;
      }
      return entry.value;
    },
    set: (key: string, value: T): void => {
      cache.set(key, { value, expiresAt: Date.now() + CACHE_TTL_MS });
    }
  };
};

const thumbnailUrlCache = makeSessionCache<string>();

interface ThumbnailState {
  url: string | undefined;
  isLoading: boolean;
}

const useMetaThumbnailUrl = (
  company: string | null,
  accountId: string,
  adId: string
): ThumbnailState => {
  const [url, setUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const cacheKey = `${company}/${accountId}/${adId}`;
    const cached = company ? thumbnailUrlCache.get(cacheKey) : undefined;

    if (cached) {
      setUrl(cached);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      (async () => {
        if (!company) {
          setIsLoading(false);
          return;
        }
        try {
          const key =
            company === DEMO_COMPANY
              ? DEMO_THUMBNAIL_KEY
              : `creativeAssets/metaThumbnails/${company}/${accountId}/${adId}.jpeg`;
          const signedUrl = await GetS3SignedUrl2(CREATIVE_BUCKET_NAME, key);
          thumbnailUrlCache.set(cacheKey, signedUrl);
          if (isMounted) {
            setUrl(signedUrl);
          }
        } catch {
          // Thumbnail fetch failures are non-fatal — fall back to the format icon.
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      })();
    }

    return () => {
      isMounted = false;
    };
  }, [company, accountId, adId]);

  return { url, isLoading };
};

interface CreativeCardMediaProps {
  company: string | null;
  accountId: string;
  adId: string;
  name: string;
  icon: React.ReactNode;
}

const CreativeCardMedia = ({ company, accountId, adId, name, icon }: CreativeCardMediaProps) => {
  const { url, isLoading } = useMetaThumbnailUrl(company, accountId, adId);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [url]);

  if (isLoading) return <Spinner size={20} />;
  if (!url || imgFailed) return <>{icon}</>;
  return <img src={url} alt={name} onError={() => setImgFailed(true)} />;
};

interface CreativeCardProps {
  icon: React.ReactNode;
  name: string;
  ctr: string;
  cpx: string;
  share?: string; // set for the top-spending creatives that drive the set
  company: string | null;
  accountId: string;
  adId: string;
}

const CreativeCard = ({
  icon,
  name,
  ctr,
  cpx,
  share,
  company,
  accountId,
  adId
}: CreativeCardProps) => (
  <div className='bp-creative-card'>
    <div className='bp-creative-card__media'>
      <CreativeCardMedia
        company={company}
        accountId={accountId}
        adId={adId}
        name={name}
        icon={icon}
      />
      {share && <span className='bp-low-div-badge'>{share} of spend</span>}
    </div>
    <div className='bp-creative-card__body'>
      <div className='bp-creative-card__name'>{name}</div>
      <div className='bp-creative-card__perf'>
        CTR {ctr} · CPX {cpx}
      </div>
    </div>
  </div>
);

interface CreativesGroupProps {
  title: string;
  creatives: Ad[];
  showShare?: boolean;
  company: string | null;
  accountId: string;
}

const CreativesGroup = ({
  title,
  creatives,
  showShare = false,
  company,
  accountId
}: CreativesGroupProps) => {
  if (creatives.length === 0) return null;

  return (
    <div className='bp-creatives-group'>
      <div className='bp-creatives-group__title'>
        {title} · {creatives.length}
      </div>
      <div className='bp-creatives-grid'>
        {creatives.map(creative => (
          <CreativeCard
            key={creative.adId}
            icon={formatIcon(creative.format)}
            name={creative.name}
            ctr={formatCtr(creative.ctr ?? 0)}
            cpx={CPX_FORMATTER(creative.cpx ?? 0)}
            share={showShare ? `${Math.round(creative.spendShare * 100)}%` : undefined}
            company={company}
            accountId={accountId}
            adId={creative.adId}
          />
        ))}
      </div>
    </div>
  );
};

type CreativesModalTarget =
  { kind: 'adSet'; adSet: DiversityAdSet } | { kind: 'alert'; alert: Alert };

interface CreativesModalProps {
  target: CreativesModalTarget | null;
  heatDomains: HeatDomains;
  company: string | null;
  onHide: () => void;
  container: RefObject<HTMLDivElement | null>;
}

const CreativesModal = ({
  target,
  heatDomains,
  company,
  onHide,
  container
}: CreativesModalProps) => {
  const isAlert = target?.kind === 'alert';
  const adSet = target?.kind === 'adSet' ? target.adSet : null;
  const alert = target?.kind === 'alert' ? target.alert : null;

  const ads = adSet?.ads ?? [];
  const driving = adSet?.creativesDriving ?? 0;
  const topCreatives = ads.slice(0, driving);
  const tailCreatives = ads.slice(driving);
  const drivingShare = Math.round((adSet?.spendShare ?? 0) * 100);

  return (
    <Modal
      show={Boolean(target)}
      onHide={onHide}
      container={container}
      centered
      scrollable
      size={isAlert ? 'lg' : undefined}
      className={isAlert ? 'bp-alert-modal' : 'bp-creatives-modal'}
    >
      {isAlert ? (
        <>
          <BpModalHeader title='Diversity alert' subtitles={[alert?.summary]} />
          <Modal.Body className='bp-modal__body'>
            <table className='bp-table'>
              <thead>
                <tr>
                  <th>Ad set</th>
                  <th>Diversity</th>
                  <th>CTR</th>
                  <th>CPX</th>
                </tr>
              </thead>
              <tbody>
                {alert?.adSets.map(alertAdSet => (
                  <tr key={alertAdSet.adSetId}>
                    <td>
                      <span className='bp-table__name'>{alertAdSet.name}</span>
                      <span className='bp-set-link'>{alertAdSet.campaign}</span>
                    </td>
                    <td className='bp-heat'>
                      <span
                        className='bp-heat__fill'
                        style={{
                          backgroundColor: getHeatColor(
                            heatDomains,
                            'diversity',
                            alertAdSet.diversityScore
                          )
                        }}
                      >
                        {alertAdSet.diversityScore.toFixed(2)} · {alertAdSet.diversityLabel}
                      </span>
                    </td>
                    <td>{formatCtr(alertAdSet.ctr ?? 0)}</td>
                    <td>{CPX_FORMATTER(alertAdSet.cpx ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Modal.Body>
        </>
      ) : (
        <>
          {/* Campaign is a subtitle here rather than a table column: ad-set names repeat
              across campaigns, so it's the disambiguator, but it made the column noisy. */}
          <BpModalHeader
            title={`Creatives · ${adSet?.name}`}
            subtitles={[
              adSet?.campaign,
              `${adSet?.activeAds} active ads · top ${driving} drive ${drivingShare}% of spend`
            ]}
          />
          <Modal.Body className='bp-modal__body'>
            <CreativesGroup
              title='Top creatives by spend'
              creatives={topCreatives}
              showShare
              company={company}
              accountId={adSet?.account ?? ''}
            />
            <CreativesGroup
              title='Long tail'
              creatives={tailCreatives}
              company={company}
              accountId={adSet?.account ?? ''}
            />
            <p className='bp-modal__hint'>
              Active ads only · a few creatives carrying most of the spend is the concentration risk
            </p>
          </Modal.Body>
        </>
      )}
    </Modal>
  );
};

// ── Ad creative modal ────────────────────────────────────────────────────────
interface AdPreviewFields {
  src: string;
  width: number;
  height: number;
}

const AD_PREVIEW_DISPLAY_WIDTH = 348;
const AD_PREVIEW_DISPLAY_HEIGHT = 448;

const adPreviewCache = makeSessionCache<AdPreviewFields>();

interface AdPreviewState {
  preview: AdPreviewFields | undefined;
  isLoading: boolean;
}

const useMetaAdPreview = (accountId: string | null, adId: string | undefined): AdPreviewState => {
  const [preview, setPreview] = useState<AdPreviewFields | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const cacheKey = `${accountId}/${adId}`;
    const cached = accountId && adId ? adPreviewCache.get(cacheKey) : undefined;

    if (cached) {
      setPreview(cached);
      setIsLoading(false);
    } else {
      setPreview(undefined); // reset so switching ads doesn't briefly show the previous preview
      setIsLoading(true);
      (async () => {
        if (!accountId || !adId) {
          setIsLoading(false);
          return;
        }
        try {
          const res = await MetaLambdaFetch('/getAdPreview', {
            method: 'POST',
            body: { accountID: accountId, businessManager: 'Tinuiti', adMetaId: adId }
          });
          const parsed = await awaitJSON(res);
          const fields: AdPreviewFields = {
            src: parsed.src,
            width: Number(parsed.width),
            height: Number(parsed.height)
          };
          adPreviewCache.set(cacheKey, fields);
          if (isMounted) {
            setPreview(fields);
          }
        } catch {
          // Preview fetch failures are non-fatal — fall back to the format icon.
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      })();
    }

    return () => {
      isMounted = false;
    };
  }, [accountId, adId]);

  return { preview, isLoading };
};

interface AdPreviewMediaProps {
  company: string | null;
  accountId: string | null;
  ad: FatigueAd;
}

const AdPreviewMedia = ({ company, accountId, ad }: AdPreviewMediaProps) => {
  // Demo ads don't exist in Meta, so there's no live preview to fetch; show the placeholder.
  if (company === DEMO_COMPANY) {
    return (
      <CreativeCardMedia
        company={company}
        accountId={accountId ?? ''}
        adId={ad.adId}
        name={ad.name}
        icon={formatIcon(ad.format)}
      />
    );
  }
  return <LiveAdPreviewMedia accountId={accountId} ad={ad} />;
};

interface LiveAdPreviewMediaProps {
  accountId: string | null;
  ad: FatigueAd;
}

const LiveAdPreviewMedia = ({ accountId, ad }: LiveAdPreviewMediaProps) => {
  const { preview, isLoading } = useMetaAdPreview(accountId, ad.adId);

  if (isLoading) return <Spinner size={28} />;
  if (!preview) return <>{formatIcon(ad.format)}</>;

  const w = preview.width;
  const h = preview.height;
  const scale = Math.min(AD_PREVIEW_DISPLAY_WIDTH / w, AD_PREVIEW_DISPLAY_HEIGHT / h, 1);
  const displayWidth = Math.round(w * scale);
  const displayHeight = Math.round(h * scale);

  return (
    <div
      className='bp-ad-creative__iframe-outer'
      style={{ width: displayWidth, height: displayHeight }}
    >
      <iframe
        className='bp-ad-creative__iframe'
        src={preview.src}
        title={ad.name}
        width={w}
        height={h}
        style={{
          width: w,
          height: h,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          border: 0
        }}
      />
    </div>
  );
};

interface AdCreativeModalProps {
  company: string | null;
  ad: FatigueAd | null;
  adSet: FatigueAdSet | null;
  onHide: () => void;
  container: RefObject<HTMLDivElement | null>;
}

const AdCreativeModal = ({ company, ad, adSet, onHide, container }: AdCreativeModalProps) => (
  <Modal
    show={Boolean(ad)}
    onHide={onHide}
    container={container}
    centered
    className='bp-ad-creative-modal'
  >
    <BpModalHeader
      title={ad?.name}
      subtitles={[
        adSet?.name,
        adSet && (
          <span style={{ color: statusColorFor(adSet.status?.active ?? null) }}>
            {formatAdSetStatus(adSet)}
          </span>
        )
      ]}
    />
    <Modal.Body className='bp-modal__body'>
      {ad && (
        <div className='bp-ad-creative'>
          <div className='bp-ad-creative__media'>
            <AdPreviewMedia company={company} accountId={adSet?.account ?? null} ad={ad} />
          </div>
          <table className='bp-table bp-ad-creative__metrics'>
            <tbody>
              {/* <tr>
                <td className='bp-cell-left'>Format</td>
                <td>{ad.format}</td>
              </tr> */}
              <tr>
                <td className='bp-cell-left'>Spend</td>
                <td>{formatSpend(ad.spend)}</td>
              </tr>
              <tr>
                <td className='bp-cell-left'>Share of spend</td>
                <td>{Math.round(ad.spendShare * 100)}%</td>
              </tr>
              <tr>
                <td className='bp-cell-left'>CTR</td>
                <td>{formatCtr(ad.ctr ?? 0)}</td>
              </tr>
              <tr>
                <td className='bp-cell-left'>ROAS</td>
                <td>{formatRoas(ad.roas ?? 0)}</td>
              </tr>
              <tr>
                <td className='bp-cell-left'>CPM</td>
                <td>{CPM_FORMATTER(ad.cpm ?? 0)}</td>
              </tr>
              <tr>
                <td className='bp-cell-left'>CPX</td>
                <td>{CPX_FORMATTER(ad.cpx ?? 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Modal.Body>
  </Modal>
);

// ── Page body ────────────────────────────────────────────────────────────────
// Branches the mutually-exclusive initial-load / loaded states so `<Page>` (nav pills,
// KPI picker, date picker, filter bar) stays mounted while the first load is in flight,
// instead of the whole page swapping out for a spinner.

interface MetaFatigueInsightsBodyProps {
  isLoading: boolean;
  children: ReactNode;
}

const MetaFatigueInsightsBody = ({ isLoading, children }: MetaFatigueInsightsBodyProps) => {
  if (isLoading) return <Spinner />;
  return <>{children}</>;
};

const DEMO_KPI_PICKER_DATA = {
  kpiMap: buildCrossChannelKpiMap(DEMO_KPIS),
  defaultKpi: DEMO_KPIS[0]
};

// ── Page root ──────────────────────────────────────────────────────────────

export const MetaFatigueInsights = () => {
  const { tab, goToTab } = useTabbedNav<TabType>({
    baseURL: 'meta-creative-health-dashboard',
    defaultKey: Tab.COMBINED,
    navs: TAB_NAVS.map(nav => nav.key)
  });
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(() => new Set());
  const [activeModal, setActiveModal] = useState<CreativesModalTarget | null>(null);
  const [isolatedAdSetId, setIsolatedAdSetId] = useState<string | null>(null);
  const [activeAd, setActiveAd] = useState<{ ad: FatigueAd; adSet: FatigueAdSet } | null>(null);
  // Modals portal here so the page-scoped (.meta-fatigue-insights) styles reach them.
  const pageRef = useRef<HTMLDivElement>(null);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [filterState, setFilterState] = useFilterPaneState();
  // Filter-preset plumbing. FetchFilterBar owns the fetching, saving and deleting; it just
  // needs somewhere to keep the results, which is what these three hold. `filters` starts
  // undefined on purpose — that is the signal the bar watches to trigger its own
  // `/getFilters` call (which also needs `company` and `platform`, both passed below).
  const [filters, setFilters] = useState<FilterPreset[] | undefined>();
  const [filterID, setFilterID] = useState<number | undefined>();
  const [filterSource, setFilterSource] = useState<FilterSourceType>(FilterSource.CURRENT_STATE);

  const closeModals = useCallback(() => {
    setActiveModal(null);
    setActiveAd(null);
  }, []);

  const company = useLocationCompany();
  const setError = useSetError();

  const [kpi, setKpi] = useState<string>('');
  const companyKpis = useKpiPickerData({
    company: company ?? undefined,
    useCrossChannelKpis: true
  });
  const { kpiMap, defaultKpi } = company === DEMO_COMPANY ? DEMO_KPI_PICKER_DATA : companyKpis;
  // Re-seed whenever the selected KPI isn't in the current map, not only when none is selected:
  // company info can arrive after a KPI was already picked, and the map can change under it.
  const isKpiInMap = Boolean(kpi) && Boolean(kpiMap[kpi]);
  useEffect(() => {
    if (!isKpiInMap && defaultKpi) {
      setKpi(defaultKpi);
    }
  }, [defaultKpi, isKpiInMap]);

  const {
    data: diversity = [],
    isPending: isLoadingDiversity,
    error: diversityError
  } = useDiversityLabelScore(company);
  const {
    data: fatigueSummary = [],
    isPending: isLoadingFatigueSummary,
    error: fatigueSummaryError
  } = useCreativeFatigueSummary(company);
  const {
    data: fatiguedAds = [],
    isPending: isLoadingFatiguedAds,
    error: fatiguedAdsError
  } = useCreativeFatiguedAds(company);
  const {
    data: adsetStatus = [],
    isPending: isLoadingAdsetStatus,
    error: adsetStatusError
  } = useAdsetStatus(company);
  const {
    data: adSetTotals = [],
    isPending: isLoadingAdSetTotals,
    isFetching: isFetchingAdSetTotals,
    error: adSetTotalsError
  } = useAdSetPerformanceTotals(company, dateRange);
  const {
    data: adTotals = [],
    isPending: isLoadingAdTotals,
    isFetching: isFetchingAdTotals,
    error: adTotalsError
  } = useAdPerformanceTotals(company, dateRange);
  const {
    data: adSetDailyPerf = [],
    isPending: isLoadingAdSetDailyPerf,
    isFetching: isFetchingAdSetDailyPerf,
    error: adSetDailyPerfError
  } = useAdSetDailyPerformance(company, dateRange);
  const {
    data: placementBreakdown = [],
    isPending: isLoadingPlacementBreakdown,
    isFetching: isFetchingPlacementBreakdown,
    error: placementBreakdownError
  } = useAdsetPlacementBreakdown(company, dateRange);
  const {
    data: adKpiVolumeRevenue = [],
    isPending: isLoadingAdKpiVolumeRevenue,
    isFetching: isFetchingAdKpiVolumeRevenue,
    error: adKpiVolumeRevenueError
  } = useAdKpiVolumeRevenue(company, dateRange, isKpiInMap ? kpi : ''); // '' holds the query until re-seeded

  // One effect per query, matching the repo's shared-error-modal convention — each fires
  // only when its own query settles into an error.
  useEffect(() => {
    if (diversityError) {
      setError({ message: diversityError.message, reportError: diversityError });
    }
  }, [diversityError, setError]);
  useEffect(() => {
    if (fatigueSummaryError) {
      setError({ message: fatigueSummaryError.message, reportError: fatigueSummaryError });
    }
  }, [fatigueSummaryError, setError]);
  useEffect(() => {
    if (fatiguedAdsError) {
      setError({ message: fatiguedAdsError.message, reportError: fatiguedAdsError });
    }
  }, [fatiguedAdsError, setError]);
  useEffect(() => {
    if (adsetStatusError) {
      setError({ message: adsetStatusError.message, reportError: adsetStatusError });
    }
  }, [adsetStatusError, setError]);
  useEffect(() => {
    if (adSetTotalsError) {
      setError({ message: adSetTotalsError.message, reportError: adSetTotalsError });
    }
  }, [adSetTotalsError, setError]);
  useEffect(() => {
    if (adTotalsError) {
      setError({ message: adTotalsError.message, reportError: adTotalsError });
    }
  }, [adTotalsError, setError]);
  useEffect(() => {
    if (adSetDailyPerfError) {
      setError({ message: adSetDailyPerfError.message, reportError: adSetDailyPerfError });
    }
  }, [adSetDailyPerfError, setError]);
  useEffect(() => {
    if (placementBreakdownError) {
      setError({ message: placementBreakdownError.message, reportError: placementBreakdownError });
    }
  }, [placementBreakdownError, setError]);
  useEffect(() => {
    if (adKpiVolumeRevenueError) {
      setError({ message: adKpiVolumeRevenueError.message, reportError: adKpiVolumeRevenueError });
    }
  }, [adKpiVolumeRevenueError, setError]);

  const isLoadingLiveData =
    isLoadingDiversity ||
    isLoadingFatigueSummary ||
    isLoadingFatiguedAds ||
    isLoadingAdsetStatus ||
    isLoadingAdSetTotals ||
    isLoadingAdTotals ||
    isLoadingAdSetDailyPerf ||
    isLoadingPlacementBreakdown ||
    isLoadingAdKpiVolumeRevenue;

  const isFetchingLiveData =
    isFetchingAdSetTotals ||
    isFetchingAdTotals ||
    isFetchingAdSetDailyPerf ||
    isFetchingPlacementBreakdown ||
    isFetchingAdKpiVolumeRevenue;

  const { adSets, campaigns } = useMemo(
    () =>
      buildAdSetsFromLiveData({
        adSetTotals,
        adTotals,
        adSetDailyPerf,
        diversity,
        fatigueSummary,
        fatiguedAds,
        adsetStatus,
        placementBreakdown,
        adKpiVolumeRevenue
      }),
    [
      adSetTotals,
      adTotals,
      adSetDailyPerf,
      diversity,
      fatigueSummary,
      fatiguedAds,
      adsetStatus,
      placementBreakdown,
      adKpiVolumeRevenue
    ]
  );
  const heatDomains = useMemo(() => computeHeatDomains(adSets), [adSets]);
  const alerts = useMemo(() => buildAlerts(adSets), [adSets]);
  const filterCategories = useMemo(
    () => buildFilterCategories(adSets, campaigns),
    [adSets, campaigns]
  );

  const filteredAdSets = useMemo(
    () => filterAdSets(adSets, filterCategories, filterState),
    [adSets, filterCategories, filterState]
  );
  // distinguishes "your filters excluded everything" from "there's no data here"
  const filtersActive = hasFilter(filterState);

  const handleIsolate = (adSetId: string) => setIsolatedAdSetId(adSetId);
  const handleResetIsolate = () => setIsolatedAdSetId(null);

  const isInitialLoading = isLoadingLiveData && adSets.length === 0;

  // Data is already on screen but a date-range or KPI change kicked off a refetch —
  // blank the charts/tables back to a spinner rather than silently swapping numbers
  // under the user a few seconds later. Driven by isFetching (not isLoadingLiveData/
  // isPending): the date/kpi-scoped queries keep serving their previous data as a
  // placeholder during this refetch, so isPending is already false by the time adSets
  // reflects that previous data — isFetching is what's still true for the request in flight.
  const isRefetching = isFetchingLiveData && adSets.length > 0;

  return (
    <Page
      title='Meta Creative Health Dashboard'
      pageType='Meta Creative Health Dashboard'
      navs={TAB_NAVS}
      selectedNav={tab}
      onNav={nav => {
        closeModals();
        goToTab(nav as TabType);
      }}
      isLoading={isLoadingLiveData}
      actions={
        <div className='bp-page-actions'>
          {isKpiInMap && (
            <KpiPicker
              kpi={kpi}
              onChange={newKpi => {
                closeModals();
                setKpi(newKpi);
              }}
              kpiMap={kpiMap}
              defaultKpi={defaultKpi}
              disableTypeSelector
              noBreak
            />
          )}
          <DatePicker
            range={dateRange}
            onChange={newRange => {
              closeModals();
              setDateRange(newRange);
            }}
            datePresets
            design={Design.BRAND}
          />
        </div>
      }
      subHeader={
        <FetchFilterBar
          categories={filterCategories}
          company={company ?? undefined}
          platform={FILTER_PLATFORM}
          filters={filters}
          setFilters={setFilters}
          filterID={filterID}
          setFilterID={setFilterID}
          filterSource={filterSource}
          setFilterSource={setFilterSource}
          setState={setFilterState}
          state={filterState}
          // Deliberately a no-op: other pages refetch from the backend when a filter
          // changes, but this page filters data it already holds (see its CLAUDE.md).
          refreshData={() => {}}
        />
      }
    >
      <MetaFatigueInsightsBody isLoading={isInitialLoading}>
        <div className='meta-fatigue-insights' ref={pageRef}>
          <div className='bp-shell'>
            {/* Diversity alerts surface on the Diversity tab; the Combined view shows them too. */}
            {(tab === Tab.COMBINED || tab === Tab.DIVERSITY) &&
              alerts
                .filter(alert => !dismissedAlerts.has(alert.id))
                .map(alert => (
                  <AlertBanner
                    key={alert.id}
                    alert={alert}
                    onOpen={() => setActiveModal({ kind: 'alert', alert })}
                    onDismiss={() => setDismissedAlerts(prev => new Set(prev).add(alert.id))}
                  />
                ))}

            {tab === Tab.COMBINED && (
              <CombinedPanel
                adSets={filteredAdSets}
                campaigns={campaigns}
                isolatedAdSetId={isolatedAdSetId}
                onIsolate={handleIsolate}
                onResetIsolate={handleResetIsolate}
                onNavigate={goToTab}
                dateRange={dateRange}
                filtersActive={filtersActive}
                isLoading={isRefetching}
                container={pageRef}
              />
            )}
            {tab === Tab.DIVERSITY && (
              <DiversityPanel
                adSets={filteredAdSets}
                onViewCreatives={adSet => setActiveModal({ kind: 'adSet', adSet })}
                filtersActive={filtersActive}
                isLoading={isRefetching}
              />
            )}
            {tab === Tab.FATIGUE && (
              <FatiguePanel
                adSets={filteredAdSets}
                onViewAd={(ad, adSet) => setActiveAd({ ad, adSet })}
                dateRange={dateRange}
                filtersActive={filtersActive}
                isLoading={isRefetching}
              />
            )}
          </div>

          <CreativesModal
            target={activeModal}
            heatDomains={heatDomains}
            company={company}
            onHide={() => setActiveModal(null)}
            container={pageRef}
          />
          <AdCreativeModal
            company={company}
            ad={activeAd?.ad ?? null}
            adSet={activeAd?.adSet ?? null}
            onHide={() => setActiveAd(null)}
            container={pageRef}
          />
        </div>
      </MetaFatigueInsightsBody>
    </Page>
  );
};
